<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              เพิ่มผู้ดูแลระบบ
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              เพิ่มผู้ดูแลระบบ เพื่อตั้งค่าการทำงานของระบบต่างๆ
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <ValidationProvider
              name="ที่อยู่อีเมล"
              rules="required|email"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="admin.email"
                label="ที่อยู่อีเมล"
                required
                type="text"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div v-if="formAction === 'add'">
            <ValidationProvider
              name="รหัสผ่าน"
              vid="confirmation"
              rules="required|min:6|max:128"
              v-slot="{ errors }"
            >
              <AppInput
                v-model="admin.password"
                label="รหัสผ่าน"
                required
                type="password"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div v-if="formAction === 'add'">
            <ValidationProvider
              name="ยืนยันรหัสผ่าน"
              rules="required|confirmed:confirmation"
              v-slot="{ errors }"
            >
              <AppInput
                v-model="admin.confirmPassword"
                label="ยืนยันรหัสผ่าน"
                required
                type="password"
              />
              <span class="text-base text-red-600">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>

          <div>
            <AppInput
              v-model="admin.description"
              label="คำอธิบาย"
              type="text"
            />
          </div>

          <div>
            <AppToggleInput v-model="admin.is_active" />
          </div>

          <div>
            <DateTimeInput
              v-model="admin.start_time"
              label="วันเวลาเริ่มต้นใช้งาน"
              @invalid="(value) => (startTimeValid = value)"
            />
          </div>

          <div>
            <DateTimeInput
              v-model="admin.expire_time"
              label="วันเวลาสิ้นสุดการใช้งาน"
              @invalid="(value) => (expireTimeValid = value)"
            />
          </div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-end">
          <div v-if="!isDataChange && formAction === 'edit'">
            <button
              @click="onDelete"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-red-600
                text-base
                font-medium
                text-white
                hover:bg-red-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-red-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span class="ml-2">ลบ</span>
            </button>
          </div>

          <div v-else class="space-x-4">
            <button
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>

            <button
              v-if="formAction === 'add'"
              @click="handleSubmit(onSubmit)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              ตกลง
            </button>

            <button
              v-if="formAction === 'edit'"
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import moment from 'moment'

import AppInput from '@/components/AppInput'
import AppToggleInput from '@/components/AppToggleInput'
import DateTimeInput from '@/components/DateTimeInput'
import { extend } from 'vee-validate'
import { confirmed, email, max, min, required } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})
extend('min', {
  ...min,
  params: ['length'],
  message: '{_field_} ต้องมากกว่า {length} ตัวอักษร',
})
extend('max', {
  ...max,
  params: ['length'],
  message: '{_field_} ต้องน้อยกว่า {length} ตัวอักษร',
})
extend('email', {
  ...email,
  message: '{_field_} ไม่ถูกต้อง',
})
extend('confirmed', {
  ...confirmed,
  message: '{_field_} ไม่ถูกต้อง',
})

export default {
  name: 'FormAdmin',

  props: {
    formAction: {
      type: String,
    },
    currentAdmin: {
      type: Object,
    },
    admin: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppToggleInput,
    DateTimeInput,
  },

  data() {
    return {
      loading: false,
      errors: [],
      startTimeValid: false,
      expireTimeValid: false,
    }
  },

  computed: {
    isDataChange() {
      if (
        this.currentAdmin.email !== this.admin.email ||
        this.currentAdmin.password !== this.admin.password ||
        this.currentAdmin.confirmPassword !== this.admin.confirmPassword ||
        this.currentAdmin.description !== this.admin.description ||
        this.currentAdmin.is_active !== this.admin.is_active ||
        this.currentAdmin.start_time !== this.admin.start_time ||
        this.currentAdmin.expire_time !== this.admin.expire_time
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },
  },

  methods: {
    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    async onSubmit() {
      if (this.startTimeValid || this.expireTimeValid) {
        return
      }

      try {
        this.loading = true

        await this.$store.dispatch('role/fetchRoles', 'taff_admin')

        const newAdmin = {
          email: this.admin.email,
          password: this.admin.password,
          description: this.admin.description,
          is_active: this.admin.is_active,
          role_uuid: this.$store.state.role.roles[0].role_uuid,
          start_time: this.admin.start_time
            ? moment.utc(this.admin.start_time, 'DD/MM/YYYY HH:mm').format()
            : null,
          expire_time: this.admin.expire_time
            ? moment.utc(this.admin.expire_time, 'DD/MM/YYYY HH:mm').format()
            : null,
        }

        await this.$store.dispatch('admin/addAdmin', newAdmin)

        const notification = {
          type: 'success',
          message: 'Your admin has been added!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async onSave() {
      if (this.startTimeValid || this.expireTimeValid) {
        return
      }

      try {
        this.loading = true

        const adminUpdate = {
          taff_admin_uuid: this.admin.taff_admin_uuid,
          email: this.admin.email,
          description: this.admin.description,
          is_active: this.admin.is_active,
          role_uuid: this.admin.role.role_uuid,
          start_time: this.admin.start_time
            ? moment.utc(this.admin.start_time, 'DD/MM/YYYY HH:mm').format()
            : null,
          expire_time: this.admin.expire_time
            ? moment.utc(this.admin.expire_time, 'DD/MM/YYYY HH:mm').format()
            : null,
        }

        await this.$store.dispatch('admin/editAdmin', adminUpdate)

        const notification = {
          type: 'success',
          message: 'Your admin has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(
            error.response.data.message || 'Something went wrong.'
          )
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    onDelete() {
      this.$emit('onDelete', this.admin)
    },
  },
}
</script>
